import { InfoCircleOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import {
  Breadcrumb,
  Button,
  Card,
  DatePicker,
  message,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { defaultDateFormat, formatCurrency, MODULES, ROUTES } from '../../common/constants';
import Portal from '../../components/Portal';
import { GET_ACCOUNT_LEDGER } from './graphql/Queries';

dayjs.extend(quarterOfYear);

const { RangePicker } = DatePicker;

const getStatusColor = status => {
  switch (status) {
    case 'SETTLED':
      return 'green';
    case 'PAID':
      return 'cyan';
    case 'PARTIALLY_PAID':
      return 'blue';
    case 'UNPAID':
      return 'volcano';
    default:
      return 'gray';
  }
};

const CustomerAccountLedger = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [showSettled, setShowSettled] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [sortedInfo, setSortedInfo] = useState({});

  const [
    getLedgerEntries,
    { data, loading },
  ] = useLazyQuery(GET_ACCOUNT_LEDGER, {
    fetchPolicy: 'network-only',
    onError: () => { },
  });

  const fetchLedgerEntries = () => {
    getLedgerEntries({
      variables: {
        filters: {
          accountId: id,
          showSettled,
          fromDate: dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
          toDate: dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
        },
        sort: sortedInfo.field ? {
          sortOn: sortedInfo.field,
          sortBy: sortedInfo.order === 'ascend' ? 'ASC' : 'DESC',
        } : null,
      },
    });
  };

  useEffect(() => {
    fetchLedgerEntries();
  }, [showSettled, dateRange, sortedInfo]);

  const handleTableChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
    fetchLedgerEntries();
  };

  const disableFutureDates = current => current && current > dayjs().endOf('day');

  const handleChange = dates => {
    if (dates && dates[0] && dates[1] && dates[0].isAfter(dates[1])) {
      message.error('Start date cannot be after end date');
      return;
    }
    setDateRange(dates);
  };

  const handleSettledToggle = checked => {
    setShowSettled(checked);

    if (checked) {
      const start = dayjs().startOf('quarter');
      const end = dayjs().endOf('quarter').isAfter(dayjs()) ? dayjs() : dayjs().endOf('quarter');
      setDateRange([start, end]);
    } else {
      setDateRange(null); // Clear range when switch is off
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => dayjs(date).format(defaultDateFormat),
      sorter: true,
      sortOrder: sortedInfo.columnKey === 'date' && sortedInfo.order,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt) => dayjs(createdAt).format(defaultDateFormat),
      sorter: true,
      sortOrder: sortedInfo.columnKey === 'createdAt' && sortedInfo.order,
    },
    {
      title: 'Type',
      key: 'type',
      render: (_, record) => {
        if (record.type === 'ACCOUNT_ACTION') {
          return (
            <Tag color={record.actionType === 'ACCOUNT_CLEARED' ? 'green' : 'orange'}>
              {record.actionType === 'ACCOUNT_CLEARED' ? 'Account Cleared' : 'Carry Forward'}
            </Tag>
          );
        }
        return 'Invoice';
      },
    },
    {
      title: 'ID/Details',
      key: 'details',
      render: (_, record) => {
        if (record.type === 'ACCOUNT_ACTION') {
          return (
            <div>
              <div>{record.remarks}</div>
              <div style={{ fontSize: '0.8em', color: '#666' }}>
                By: {record.performedBy?.firstName} {record.performedBy?.lastName}
              </div>
            </div>
          );
        }
        return (
          <div>
            <div>{record.code}</div>
            {record.billNo && <div>Bill: {record.billNo}</div>}
            {record.lrNo && <div>LR: {record.lrNo}</div>}
          </div>
        );
      },
    },
    {
      title: 'Amount (₹)',
      key: 'amount',
      render: (_, record) => (
        record.type === 'TRANSACTION' ? formatCurrency(record.originalAmount) : '-'
      ),
      align: 'right',
    },
    {
      title: 'Paid (₹)',
      key: 'paid',
      render: (_, record) => (
        record.type === 'TRANSACTION' ? formatCurrency(record.settledAmount) : '-'
      ),
      align: 'right',
    },
    {
      title: 'Pending (₹)',
      key: 'pending',
      render: (_, record) => (
        record.type === 'TRANSACTION' ? formatCurrency(record.pendingAmount) : '-'
      ),
      align: 'right',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        if (record.type === 'ACCOUNT_ACTION') return '-';

        const color = getStatusColor(record.status);
        return <Tag color={color}>{record.status}</Tag>;
      },
      filters: [
        { text: 'Settled', value: 'SETTLED' },
        { text: 'Partially Paid', value: 'PARTIALLY_PAID' },
        { text: 'Unpaid', value: 'UNPAID' },
        { text: 'Paid', value: 'PAID' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        record.type === 'TRANSACTION' && (
          <Tooltip title="View payment details">
            <InfoCircleOutlined
              style={{ color: '#1890ff', fontSize: '16px' }}
            />
          </Tooltip>
        )
      ),
    },
  ];

  const expandable = {
    expandedRowRender: (record) => {
      if (record.type !== 'TRANSACTION' || !record.paymentDetails?.length) return null;

      const paymentColumns = [
        { title: 'Date', dataIndex: 'date', key: 'date', render: date => dayjs(date).format(defaultDateFormat) },
        { title: 'Transaction ID', dataIndex: 'code', key: 'code' },
        { title: 'Amount (₹)', dataIndex: 'amount', key: 'amount', render: amount => formatCurrency(amount) },
      ];

      return (
        <div style={{ margin: 0 }}>
          <h4>Payment Details:</h4>
          <Table
            dataSource={record.paymentDetails}
            columns={paymentColumns}
            pagination={false}
            size="small"
            rowKey="transactionId"
          />
        </div>
      );
    },
    rowExpandable: record => record.type === 'TRANSACTION' && record.paymentDetails?.length > 0,
  };

  return (
    <div className="member-details">
      <Portal portalId="header-wrapper">
        <Breadcrumb
          className="d-flex align-center breadcrumb-title"
          items={[
            {
              title: (
                <h1 className="m-0 font-600">{MODULES.CUSTOMER_ACCOUNT}</h1>
              ),
              onClick: () => navigate(ROUTES.CUSTOMER_ACCOUNT),
              className: 'pointer',
            },
            {
              title: <h2 className="m-0 font-600">{data?.accountLedger?.account?.customer?.code}</h2>,
            },
            {
              title: <h2 className="m-0 font-600">Account Ledger</h2>,
            },
          ]}
        />
      </Portal>

      <Card
        title="Account Ledger"
        extra={
          <Space>
            <Switch
              checked={showSettled}
              onChange={handleSettledToggle}
              checkedChildren="Show Settled"
              unCheckedChildren="Hide Settled"
            />
            <RangePicker
              value={dateRange}
              onChange={handleChange}
              disabledDate={disableFutureDates}
              allowClear
              format="YYYY-MM-DD"
              placeholder={['Start Date', 'End Date']}
            />
            <Button onClick={fetchLedgerEntries} loading={loading}>
              Refresh
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={data?.accountLedger?.entries || []}
          rowKey={(record) => `${record.type}-${record.id}`}
          expandable={expandable}
          loading={loading}
          onChange={handleTableChange}
          rowClassName={(record) => {
            if (record.type === 'ACCOUNT_ACTION') {
              return record.actionType === 'ACCOUNT_CLEARED' ? 'account-cleared-row' : 'carry-forward-row';
            }
            return '';
          }}
        />
      </Card>
    </div>
  );
};

export default CustomerAccountLedger;